import {
  AccessibilityIcon,
  CaretSortIcon,
  MoonIcon,
  ShadowInnerIcon,
  SunIcon,
} from '@radix-ui/react-icons';

import {
  Avatar,
  AvatarFallback,
  cn,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  RabbitIcon,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@app-evolution-cms/element-ui';

import { useAuth } from '../../app/providers/auth';
import { THEME, Theme, useTheme } from '../../theme-provider';
import { useUserPreferencesState } from '../user-preferences/_states/user-preferences';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const THEMES: { value: Theme; label: string; icon: any }[] = [
  { value: THEME.LIGHT, label: 'GBM Light', icon: SunIcon },
  { value: THEME.DARK, label: 'GBM Dark', icon: MoonIcon },
  { value: THEME.COLORBLIND, label: 'Daltonismo', icon: AccessibilityIcon },
  { value: THEME.TEMPORAL, label: 'GiBiMotchi', icon: RabbitIcon },
  { value: THEME.SYSTEM, label: 'Automático', icon: ShadowInnerIcon },
];

export function NavFooter() {
  const { userIdentity, logout } = useAuth();
  const email = userIdentity?.email ?? '';
  const givenName = userIdentity?.givenName ?? '';

  const { setTheme, theme } = useTheme();

  const gibimotchi = useUserPreferencesState((s) => s.gibimotchi);
  const updateUserPreferences = useUserPreferencesState((s) => s.updateUserPreferences);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton id="PER-navigation-sidebarFooter-nickUser" size="lg">
              <NickName email={email} givenName={givenName} />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="font-normal">
              <NickName email={email} givenName={givenName} type="menu" />
            </DropdownMenuLabel>

            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger id="PER-navigation-sidebarFooter-theme">
                Tema
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent sideOffset={8} align="start" alignOffset={-15}>
                  {THEMES.map(({ value, label, icon: Icon }) => (
                    <DropdownMenuItem
                      key={value}
                      id={`PER-navigation-sidebarFooter-theme-${value}`}
                      className={cn('cursor-pointer', { 'text-accent': theme === value })}
                      onClick={() => setTheme(value)}
                    >
                      <Icon className="size-4" />
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            <DropdownMenuCheckboxItem
              className="cursor-pointer"
              checkPosition="right"
              checked={gibimotchi}
              onCheckedChange={(checked) => updateUserPreferences({ gibimotchi: checked })}
            >
              GiBiMotchi
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />

            <DropdownMenuItem
              id="PER-navigation-sidebarFooter-logout"
              className="cursor-pointer"
              onClick={logout}
            >
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

interface NickNameProps {
  email: string;
  givenName: string;
  type?: 'trigger' | 'menu';
}

function getAvatarHexColor(initial: string) {
  const safeInitial = initial || '?';
  const seed = (safeInitial.charCodeAt(0) * 1234567) % 0xffffff;
  return `#${seed.toString(16).padStart(6, '0')}`;
}

function NickName({ email, givenName, type = 'trigger' }: NickNameProps) {
  const initial = givenName.trim().charAt(0).toUpperCase() || '?';

  return (
    <div
      className={cn('flex w-full items-center gap-4', {
        'py-2 pr-2 pl-1': type === 'menu',
      })}
    >
      <Avatar className="size-8">
        <AvatarFallback
          className="text-lg"
          style={{ backgroundColor: `${getAvatarHexColor(initial)}80` }}
        >
          {initial}
        </AvatarFallback>
      </Avatar>
      <div className="grid max-w-48 flex-1 text-left text-sm leading-tight">
        <span className="truncate font-medium">
          {type === 'menu' ? givenName : givenName.trim().split(' ', 1)[0]}
        </span>
        {type === 'menu' ? (
          <span className="truncate text-xs text-muted-foreground">{email.trim()}</span>
        ) : null}
      </div>
      {type === 'trigger' ? <CaretSortIcon className="ml-auto size-4" /> : null}
    </div>
  );
}
